import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi
} from "@/components/ui/carousel";
import { cn } from "@/lib/utils";

type AuthEntry = "signIn" | "signUp";

interface LandingCarouselProps {
  /** 「ログイン」または「初めての方」押下時に呼ばれる */
  onStart: (entry: AuthEntry) => void;
}

type Slide = {
  title: string;
  imagePath: string;
  imageAlt: string;
};

const SLIDES: Slide[] = [
  {
    title: "スワイプで週末のレパートリーがどんどん見つかる！",
    imagePath: "/images/landing/slide-1.jpg",
    imageAlt: "Refloop の使い方 - スワイプで探す"
  },
  {
    title: "LINEに毎週おすすめスポットが届いて、検索から解放される！",
    imagePath: "/images/landing/slide-2.jpg",
    imageAlt: "Refloop の使い方 - LINE配信"
  }
];

/**
 * ログイン前に表示するランディングカルーセル。
 * アプリの使い方をスワイプ / 矢印ボタンで紹介し、ログイン / 新規登録への
 * 導線を提供する。
 */
export const LandingCarousel = ({ onStart }: LandingCarouselProps) => {
  const [api, setApi] = useState<CarouselApi>();
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    if (!api) return;
    setSelectedIndex(api.selectedScrollSnap());
    const onSelect = () => setSelectedIndex(api.selectedScrollSnap());
    api.on("select", onSelect);
    return () => {
      api.off("select", onSelect);
    };
  }, [api]);

  const arrowClassName =
    "h-10 w-10 border-0 bg-gray-800/70 text-white shadow-md hover:bg-gray-800 hover:text-white disabled:opacity-0";

  return (
    <div className="mx-auto w-full max-w-md px-6">
      <Carousel className="overflow-hidden" opts={{ loop: false }} setApi={setApi}>
        <CarouselContent>
          {SLIDES.map((slide, slideIndex) => (
            <CarouselItem key={slideIndex}>
              <div className="flex flex-col items-center py-4">
                <h2 className="mb-4 px-4 text-center text-lg font-bold leading-relaxed">
                  {slide.title}
                </h2>
                <div className="flex items-center justify-center px-4">
                  <img
                    src={slide.imagePath}
                    alt={slide.imageAlt}
                    className="h-auto max-h-[400px] w-full object-contain"
                    loading={slideIndex === 0 ? "eager" : "lazy"}
                  />
                </div>
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
        <CarouselPrevious className={cn("left-0", arrowClassName)} />
        <CarouselNext className={cn("right-0", arrowClassName)} />
      </Carousel>

      <div className="mt-4 flex justify-center gap-2">
        {SLIDES.map((_, index) => (
          <button
            key={index}
            type="button"
            aria-label={`スライド${index + 1}へ`}
            onClick={() => api?.scrollTo(index)}
            className={cn(
              "h-2 rounded-full transition-all",
              index === selectedIndex ? "w-6 bg-primary" : "w-2 bg-muted-foreground/30"
            )}
          />
        ))}
      </div>

      <div className="mt-8 flex flex-col gap-3 pb-6">
        <Button className="w-full rounded-full" onClick={() => onStart("signUp")}>
          初めての方
        </Button>
        <Button variant="outline" className="w-full rounded-full" onClick={() => onStart("signIn")}>
          ログイン
        </Button>
      </div>
    </div>
  );
};
